import React, { useState, useEffect } from "react";
import "./Capital.css";
import Properties from "../images/properties.png"

const qualifyingItems = [
  "Heating and hot-water systems",
  "Electrical installations and lighting",
  "Lifts and nurse-call systems",
  "Commercial kitchens and laundries",
  "Fire alarms and security systems",
  "Sanitary installations and fitted equipment",
];

const processSteps = [
  {
    number: "01",
    icon: "▧",
    title: "Eligibility review",
    text: "We check ownership, business use, purchase documents, prior claims and Section 198 information before recommending a survey.",
  },
  {
    number: "02",
    icon: "▥",
    title: "Survey & analysis",
    text: "Where needed, we inspect the home and identify qualifying assets without disrupting residents or day-to-day care.",
  },
  {
    number: "03",
    icon: "▦",
    title: "Tax calculation",
    text: "Expenditure is apportioned and placed into the appropriate capital-allowance pools with a transparent methodology.",
  },
  {
    number: "04",
    icon: "☑",
    title: "Accountant handover",
    text: "You receive a detailed report and supporting schedules ready for your accountant to review and include in the tax return.",
  },
];

const faqs = [
  {
    question: "Can I claim if I bought the care home several years ago?",
    answer:
      "Potentially, yes. Previous expenditure may still qualify depending on the circumstances, ownership history and available documentation.",
  },
  {
    question: "Does a surveyor need to visit the care home?",
    answer:
      "A survey may be recommended where a detailed assessment is required. We aim to keep the process efficient and minimise disruption.",
  },
  {
    question: "Will the survey disrupt residents or staff?",
    answer:
      "No. Any site assessment is planned carefully around your operation and is designed to minimise disruption to residents and staff.",
  },
  {
    question: "What documents will you need?",
    answer:
      "Typically, we may need purchase documents, property information, refurbishment records, invoices and relevant historical expenditure details.",
  },
];

const Capital_Allowance = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const [purchasePrice, setPurchasePrice] = useState(2000000);
  const [qualifyingRate, setQualifyingRate] = useState(25);
  const [taxRate, setTaxRate] = useState(19);

  const qualifyingAmount = Number(purchasePrice || 0) * (qualifyingRate / 100);

  const taxValue = qualifyingAmount * (taxRate / 100);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
      maximumFractionDigits: 0,
    }).format(value);
  };

  // open page on top //
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // open page on top //

  return (
    <main className="capital-page">
      {/* ================= HERO ================= */}
      <section className="capital-hero">
        <div className="capital-container capital-hero-grid">
          <div className="capital-hero-content">
            <h1>
              Your care home could be holding <em>unclaimed tax relief.</em>
            </h1>

            <p className="capital-hero-text">
              We identify qualifying fixtures within care homes, calculate a
              robust claim and work with your accountant to turn overlooked
              expenditure into valuable tax relief.
            </p>

            <div className="capital-hero-buttons">
              <button className="capital-primary-btn">
                <a href="#estimator">
                  {" "}
                  Check my property <span>→</span>
                </a>
              </button>

              <button className="capital-link-btn">
                Estimate potential relief <span>→</span>
              </button>
            </div>

            <div className="capital-hero-features">
              <span>✓ Care-sector specialists</span>
              <span>✓ HMRC-ready evidence</span>
              <span>✓ Works with your accountant</span>
            </div>
          </div>

          {/* Property Review Card */}
          <div className="capital-property-wrapper">
            <div className="capital-property-card">
              <div className="capital-property-header">
                <span>CARE HOME ASSET REVIEW</span>
                <small>In review</small>
              </div>

              <div className="capital-property-image">
                <div className="capital-building">
                  <img src={Properties} alt="" />
                </div>

                <div className="capital-image-tags">
                  <span>♨ Heating</span>
                  <span>▦ Electrical</span>
                  <span>▤ Fixtures</span>
                </div>
              </div>

              <div className="capital-property-footer">
                <div>
                  <small>Assets identified</small>
                  <strong>48</strong>
                </div>

                <div>
                  <small>Claim status</small>
                  <strong>Eligible</strong>
                </div>
              </div>
            </div>

            <div className="capital-review-badge">
              <span>♢</span>

              <div>
                <strong>Specialist review</strong>
                <small>Evidence-led & defensible</small>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= INFO BAR ================= */}
      <section className="capital-info-bar">
        <div className="capital-container capital-info-grid">
          <div className="capital-info-heading">
            BUILT FOR CARE-HOME OWNERS, OPERATORS AND INVESTORS
          </div>

          <div className="capital-info-item">
            <strong>Purchase</strong>
            <span>Review embedded fixtures</span>
          </div>

          <div className="capital-info-item">
            <strong>Refurbishment</strong>
            <span>Capture improvement costs</span>
          </div>

          <div className="capital-info-item">
            <strong>Historic spend</strong>
            <span>Find missed allowances</span>
          </div>
        </div>
      </section>

      {/* ================= QUALIFYING ================= */}
      <section className="capital-qualify-section">
        <div className="capital-container capital-two-column">
          <div className="capital-copy">
            <span className="capital-eyebrow">WHAT MAY QUALIFY</span>

            <h2>
              More than furniture
              <br />
              and equipment
            </h2>

            <p>
              A significant part of a care home’s value can sit in specialist
              systems and fixtures that are easily missed in standard accounts.
            </p>

            <p className="capital-small-text">
              We separate qualifying plant and machinery from the land and
              building, allocate expenditure to the correct tax pools and
              produce the supporting methodology your accountant needs.
            </p>

            <button className="capital-underlined-btn">
              Discuss your care home <span>→</span>
            </button>
          </div>

          <div className="capital-qualifying-list">
            {qualifyingItems.map((item, index) => (
              <div className="capital-qualifying-row" key={item}>
                <span className="capital-number">
                  {String(index + 1).padStart(2, "0")}
                </span>

                <span>{item}</span>

                <strong>✓</strong>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= PROCESS ================= */}
      <section className="capital-process-section">
        <div className="capital-container">
          <div className="capital-process-heading">
            <span className="capital-eyebrow">
              A CLEAR, EVIDENCE-LED PROCESS
            </span>

            <h2>
              From property documents to
              <br />a completed claim
            </h2>

            <p>
              We coordinate the specialist property analysis while keeping you
              and your accountant informed at every stage.
            </p>
          </div>

          <div className="capital-process-grid">
            {processSteps.map((step) => (
              <article className="capital-process-card" key={step.number}>
                <div className="capital-process-icon">{step.icon}</div>

                <span className="capital-step-number">{step.number}</span>

                <h3>{step.title}</h3>

                <p>{step.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ================= ESTIMATOR ================= */}
      <section className="capital-estimator-section" id="estimator">
        <div className="capital-container capital-estimator-grid">
          <div className="capital-estimator-content">
            <span className="capital-light-eyebrow">INDICATIVE ESTIMATOR</span>

            <h2>
              See what a claim could
              <br />
              mean for your care
              <br />
              home
            </h2>

            <p>
              This quick estimate illustrates the potential scale of qualifying
              expenditure. It is not a valuation or tax advice.
            </p>

            <div className="capital-estimator-note">
              <span>♜</span>

              <div>
                <strong>Property-specific assessment</strong>

                <small>
                  Actual results depend on the building, transaction history and
                  available evidence.
                </small>
              </div>
            </div>
          </div>

          {/* Calculator */}
          <div className="capital-calculator">
            <label>Property purchase price</label>

            <div className="capital-price-input">
              <span>£</span>

              <input
                type="number"
                value={purchasePrice}
                onChange={(e) => setPurchasePrice(e.target.value)}
              />
            </div>

            <label>Estimated qualifying proportion</label>

            <div className="capital-options capital-three-options">
              {[15, 25, 35].map((rate) => (
                <button
                  key={rate}
                  className={qualifyingRate === rate ? "active" : ""}
                  onClick={() => setQualifyingRate(rate)}
                >
                  {rate}%
                </button>
              ))}
            </div>

            <label>Indicative tax rate</label>

            <div className="capital-options capital-two-options">
              {[19, 25].map((rate) => (
                <button
                  key={rate}
                  className={taxRate === rate ? "active" : ""}
                  onClick={() => setTaxRate(rate)}
                >
                  {rate}%
                </button>
              ))}
            </div>

            <div className="capital-calculator-results">
              <div>
                <small>Potential qualifying expenditure</small>

                <strong>{formatCurrency(qualifyingAmount)}</strong>
              </div>

              <div>
                <small>Indicative tax value</small>

                <strong>{formatCurrency(taxValue)}</strong>
              </div>
            </div>

            <button className="capital-calculator-button">
              Request an eligibility review
              <span>→</span>
            </button>

            <small className="capital-disclaimer">
              Illustration only. Tax relief is normally recovered over time
              through the applicable allowances and depends on your tax
              position.
            </small>
          </div>
        </div>
      </section>

      {/* ================= WHY TOTAL CARE ================= */}
      <section className="capital-why-section">
        <div className="capital-container">
          <div className="capital-why-box">
            <div className="capital-why-heading">
              <span className="capital-light-eyebrow">
                WHY TOTAL CARE ADVISORY
              </span>

              <h2>
                Property tax expertise
                <br />
                with care-sector
                <br />
                understanding.
              </h2>
            </div>

            <div className="capital-why-list">
              <div className="capital-why-item">
                <span>✓</span>

                <div>
                  <strong>We understand care homes</strong>

                  <p>
                    From resident areas and commercial kitchens to specialist
                    care systems.
                  </p>
                </div>
              </div>

              <div className="capital-why-item">
                <span>✓</span>

                <div>
                  <strong>We start with eligibility</strong>

                  <p>
                    We review the transaction history before unnecessary survey
                    work.
                  </p>
                </div>
              </div>

              <div className="capital-why-item">
                <span>✓</span>

                <div>
                  <strong>We make the claim usable</strong>

                  <p>
                    Clear schedules and evidence designed to work with your
                    accountant.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="capital-faq-section">
        <div className="capital-container capital-two-column">
          <div className="capital-faq-heading">
            <span className="capital-eyebrow">FREQUENTLY ASKED QUESTIONS</span>

            <h2 className="cap-faq-heading">
              What care-home
              owners ask us
            </h2>
          </div>

          <div className="capital-faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;

              return (
                <div
                  className={`capital-faq-item ${isOpen ? "open" : ""}`}
                  key={faq.question}
                >
                  <button
                    className="capital-faq-question"
                    onClick={() => setOpenFaq(isOpen ? null : index)}
                  >
                    <span>{faq.question}</span>

                    <strong>{isOpen ? "−" : "⌄"}</strong>
                  </button>

                  {isOpen && (
                    <div className="capital-faq-answer">
                      <p>{faq.answer}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Capital_Allowance;
